import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../utils/api';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const UsersPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'USER', status: 'Active' });
  
  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleEdit = (user) => {
    setEditingUser({ ...user });
  };
  
  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let response;
        
        // For company admins, get users from their company
        if (currentUser.role === 'COMPANY_ADMIN') {
          response = await api.get(`/api/companies/${currentUser.companyId}/users`);
        } else {
          // For regular users, they should only see themselves
          response = await api.get('/api/users');
        }
        
        // Format user data
        const formattedUsers = response.data.map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          status: user.isActive ? 'Active' : 'Inactive',
          lastLogin: user.lastLogin ? formatDistanceToNow(parseISO(user.lastLogin), { addSuffix: true }) : 'Never'
        }));
        
        setUsers(formattedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [currentUser]);

  const handleAddUser = async () => {
    try {
      setLoading(true);
      
      // Prepare user data
      const userData = {
        firstName: newUser.name.split(' ')[0] || '',
        lastName: newUser.name.split(' ').slice(1).join(' ') || '',
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        isActive: newUser.status === 'Active'
      };
      
      // Add user to the company
      const response = await api.post(`/api/companies/${currentUser.companyId}/users`, userData);
      
      // Format the new user
      const formattedUser = {
        id: response.data.id,
        name: `${response.data.firstName} ${response.data.lastName}`,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        role: response.data.role,
        status: response.data.isActive ? 'Active' : 'Inactive',
        lastLogin: 'Never'
      };
      
      setUsers([...users, formattedUser]);
      setNewUser({ name: '', email: '', password: '', role: 'USER', status: 'Active' });
      setIsAddingUser(false);
    } catch (err) {
      console.error('Error adding user:', err);
      alert(err.response?.data?.message || 'Failed to add user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // Prepare user data
      const userData = {
        firstName: editingUser.firstName || editingUser.name.split(' ')[0],
        lastName: editingUser.lastName || editingUser.name.split(' ').slice(1).join(' '),
        email: editingUser.email,
        role: editingUser.role,
        isActive: editingUser.status === 'Active'
      };
      
      // Update user
      await api.put(`/api/users/${editingUser.id}`, userData);
      
      // Update local state
      setUsers(users.map(user => user.id === editingUser.id ? editingUser : user));
      setEditingUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      alert(err.response?.data?.message || 'Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setLoading(true);
        
        // Delete user
        await api.delete(`/api/users/${id}`);
        
        // Update local state
        setUsers(users.filter(user => user.id !== id));
      } catch (err) {
        console.error('Error deleting user:', err);
        alert(err.response?.data?.message || 'Failed to delete user. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">User Management</h1>
        <div className="flex space-x-4">
          <button 
            onClick={() => navigate('/app/admin/users/invite')}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Add New User
          </button>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search Users</label>
            <input
              type="text"
              id="search"
              placeholder="Search by name or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-full md:w-48">
            <label htmlFor="role-filter" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              id="role-filter"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Roles</option>
              <option value="USER">User</option>
              <option value="COMPANY_ADMIN">Admin</option>
            </select>
          </div>
          <div className="w-full md:w-48">
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              id="status-filter"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Loading state */}
      {loading && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">Loading users...</p>
        </div>
      )}
      
      {/* Users - Card Style */}
      <div className="bg-white rounded-lg shadow">
        {/* Column Headers */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center space-x-6">
            <div className="text-sm font-medium text-gray-700 min-w-[180px]">Name</div>
            <div className="text-sm font-medium text-gray-700 min-w-[200px]">Email</div>
            <div className="text-sm font-medium text-gray-700 min-w-[80px]">Role</div>
            <div className="text-sm font-medium text-gray-700 min-w-[80px]">Status</div>
            <div className="text-sm font-medium text-gray-700 min-w-[120px]">Last Login</div>
          </div>
        </div>
        
        {/* Loading or No Items State */}
        {loading ? (
          <div className="py-10 text-center text-gray-500">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
            <p className="mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <p>No users found.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <div key={user.id} className="py-3">
                {/* User Row */}
                <div className="flex items-center justify-between py-2 px-4 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-4">
                    {/* Name */}
                    <div className="min-w-[180px]">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        {currentUser.id === user.id && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full ml-2">
                            You
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Email */}
                    <div className="text-sm text-gray-500 min-w-[200px]">
                      {user.email}
                    </div>
                    
                    {/* Role */}
                    <div className="text-sm text-gray-500 min-w-[80px]">
                      {user.role === 'COMPANY_ADMIN' ? 'Admin' : 'User'}
                    </div>
                    
                    {/* Status */}
                    <div className="min-w-[80px]">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${user.status === 'Active' ? 'bg-green-100 text-green-800' : 
                          user.status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'}`}
                      >
                        {user.status}
                      </span>
                    </div>
                    
                    {/* Last Login */}
                    <div className="text-sm text-gray-500 min-w-[120px]">
                      {user.lastLogin}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex space-x-3">
                    {/* Edit button */}
                    <button 
                      onClick={() => handleEdit(user)}
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    
                    {/* Delete button */}
                    <button 
                      onClick={() => handleDeleteUser(user.id)}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Edit User</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="edit-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="edit-email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  id="edit-role"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                >
                  <option value="USER">User</option>
                  <option value="COMPANY_ADMIN">Admin</option>
                </select>
              </div>
              <div>
                <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  id="edit-status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingUser.status}
                  onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add New User Modal */}
      {isAddingUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Add New User</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="new-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="new-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="new-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="new-email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  id="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                />
                <p className="mt-1 text-xs text-gray-500">
                  User can change their password later in settings if they wish.
                </p>
              </div>
              <div>
                <label htmlFor="new-role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  id="new-role"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="USER">User</option>
                  <option value="COMPANY_ADMIN">Admin</option>
                </select>
              </div>
              <div>
                <label htmlFor="new-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  id="new-status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newUser.status}
                  onChange={(e) => setNewUser({...newUser, status: e.target.value})}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsAddingUser(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
